import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { NavLink, Navigate, Route, Routes } from "react-router-dom"
import { Karte, Knopf, Leer } from "@/components/ui"
import { BTB_PFAD } from "@/lib/adressen"
import { api, fehlertext } from "@/lib/api"
import { cn, nf0 } from "@/lib/format"

const datumZeit = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "–"

/** Verwaltung des Betriebstagebuchs (btb.rmnc.app/verwaltung): Bediener und Chips, Excel-Import. */
export function VerwaltungPage() {
  // Feste Pfade: Relative Links lösen in einer *-Route (React Router 7) gegen die volle
  // Adresse auf und hängen sich an (/verwaltung/bediener/import …).
  const link = ({ isActive }: { isActive: boolean }) =>
    cn("rounded-md px-3 py-1.5 text-[13px] font-medium", isActive ? "bg-accent/10 text-accent-strong" : "text-ink2 hover:text-ink")
  return (
    <main className="mx-auto grid max-w-[1260px] gap-[18px] px-4 pb-12 pt-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="m-0 text-[28px] font-semibold">Verwaltung</h1>
        <nav className="flex gap-1" aria-label="Verwaltung">
          <NavLink to={`${BTB_PFAD}/verwaltung/bediener`} className={link}>Bediener und Chips</NavLink>
          <NavLink to={`${BTB_PFAD}/verwaltung/import`} className={link}>Excel-Import</NavLink>
        </nav>
      </div>
      <Routes>
        <Route index element={<Navigate to={`${BTB_PFAD}/verwaltung/bediener`} replace />} />
        <Route path="bediener" element={<BedienerSeite />} />
        <Route path="import" element={<Import />} />
      </Routes>
    </main>
  )
}

interface BedienerDaten {
  bediener: Array<{ id: number; name: string; freigegeben: boolean; chip_hinterlegt: boolean; zuletzt_angemeldet: string | null; zuletzt_abgeglichen: string | null }>
  abgleiche: Array<{ zeitpunkt: string; erfolgreich: boolean; meldung: string }>
}

function BedienerSeite() {
  const qc = useQueryClient()
  const daten = useQuery({ queryKey: ["bediener"], queryFn: async () => (await api.get<BedienerDaten>("/verwaltung/bediener")).data })
  const chips = useQuery({ queryKey: ["chips"], queryFn: async () => (await api.get<Array<{ chipnummer: string; zuletzt: string; anzahl: number }>>("/verwaltung/unbekannte-chips")).data })
  const terminalUrl =
    (import.meta.env.VITE_TERMINAL_URL as string | undefined) ||
    `${window.location.origin}${import.meta.env.BASE_URL}terminal`
  const abgleich = useMutation({
    mutationFn: async () => (await api.post<{ erfolgreich: boolean; meldung: string }>("/verwaltung/abgleich")).data,
    onSettled: () => { void qc.invalidateQueries({ queryKey: ["bediener"] }); void qc.invalidateQueries({ queryKey: ["chips"] }) },
  })
  return (
    <>
      <Karte titel="Terminal" unter="Frei aufrufbar bis zur Chipleser-Ansicht; angemeldet wird nur per Chip. Keine Einrichtung je Gerät nötig.">
        <p className="m-0 text-sm text-ink2">
          Am Gerät <span className="select-all font-mono text-ink">{terminalUrl}</span> öffnen (am besten als Vollbild/Kiosk).
          Den USB-RFID-Leser einstecken; er muss die Chipnummer mit Enter abschließen.
        </p>
      </Karte>
      <Karte
        titel="Bediener"
        unter="Freigeschaltet wird in Ninox › 01-Personal › Mitarbeiter: „Betriebstagebuch Freigabe“ einschalten und „RFID-Chip“ eintragen. Der Abgleich läuft alle 5 Minuten."
        rechts={<Knopf variante="primaer" disabled={abgleich.isPending} onClick={() => abgleich.mutate()}>{abgleich.isPending ? "Gleicht ab …" : "Jetzt mit Ninox abgleichen"}</Knopf>}
      >
        {abgleich.data && (
          <p className={cn("m-0 rounded-md px-3 py-2 text-sm", abgleich.data.erfolgreich ? "bg-good/10" : "bg-crit/10")}>{abgleich.data.meldung}</p>
        )}
        {abgleich.isError && <p className="m-0 text-sm text-crit">{fehlertext(abgleich.error)}</p>}
        {!daten.data ? <Leer>Wird geladen …</Leer> : daten.data.bediener.length === 0 ? <Leer>Noch niemand freigeschaltet.</Leer> : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead><tr className="text-left text-[11.5px] text-muted">{["Name", "Freigabe", "Chip", "Zuletzt angemeldet"].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {daten.data.bediener.map((b) => (
                  <tr key={b.id} className="border-t border-grid">
                    <td className="px-2 py-2 font-medium">{b.name}</td>
                    <td className="px-2 py-2">{b.freigegeben ? "freigegeben" : <span className="text-crit">gesperrt</span>}</td>
                    <td className="px-2 py-2">{b.chip_hinterlegt ? "hinterlegt" : <span className="text-muted">fehlt oder doppelt</span>}</td>
                    <td className="px-2 py-2 text-ink2">{datumZeit(b.zuletzt_angemeldet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {daten.data && daten.data.abgleiche.length > 0 && (
          <details className="text-[13px]">
            <summary className="cursor-pointer text-ink2">Letzte Abgleiche</summary>
            <ul className="m-0 mt-2 grid gap-1 pl-4">
              {daten.data.abgleiche.map((a) => (
                <li key={a.zeitpunkt}>{`${datumZeit(a.zeitpunkt)} · ${a.erfolgreich ? "" : "Fehler: "}${a.meldung}`}</li>
              ))}
            </ul>
          </details>
        )}
      </Karte>
      <Karte titel="Unbekannte Chips" unter="Am Terminal vorgehalten oder eingetippt, aber keinem Bediener zugeordnet. Nummer in Ninox beim Mitarbeiter unter „RFID-Chip“ eintragen; Einträge verschwinden nach dem Abgleich bzw. nach 14 Tagen.">
        {!chips.data ? <Leer>Wird geladen …</Leer> : chips.data.length === 0 ? <Leer>Keine.</Leer> : (
          <table className="w-full border-collapse text-[13px]">
            <thead><tr className="text-left text-[11.5px] text-muted">{["Chipnummer", "Zuletzt", "Versuche"].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {chips.data.map((c) => (
                <tr key={c.chipnummer} className="border-t border-grid">
                  <td className="select-all px-2 py-2 font-mono">{c.chipnummer}</td>
                  <td className="px-2 py-2">{datumZeit(c.zuletzt)}</td>
                  <td className="px-2 py-2">{c.anzahl}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Karte>
    </>
  )
}

interface Spalten {
  wo: string; anzahl: number; per_ueberschrift: number
  per_position: string[]; verschoben: string[]; fehlen: string[]
}

interface DateiBericht {
  datei: string; art: "tagebuch" | "extrakt" | null; jahr: number | null; zeilen: number
  spalten: Spalten[]; warnungen: string[]; stopp: string[]
  hinweise: { dubletten?: number; ohne_datum?: number; bis?: string | null; uebersprungen?: string }
}

interface ImportErgebnis {
  zeilen: number; neu: number; geaendert: number; unveraendert: number
  ausgeblendet?: number; wieder_eingeblendet?: number; schluessel_uebernommen?: number
  beispiele_ausgeblendet?: Array<{ datum: string; zeitfenster: string | null; mischung: string | null; nc_typ: string | null }>
  ausreisser_gesamt: number
}

interface Vorschau { dateien: DateiBericht[]; stopp: boolean; ergebnis: ImportErgebnis | null }

const datum = (iso?: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("de-DE") : "–")
const ART = { tagebuch: "Betriebstagebuch", extrakt: "Jahresextrakt" }

function formular(dateien: File[]): FormData {
  const fd = new FormData()
  for (const f of dateien) fd.append("dateien", f)
  return fd
}

/** Eine geprüfte Datei: Art, Spalten-Erkennung, Hinweise, Stopp. */
function DateiPruefung({ d }: { d: DateiBericht }) {
  const abweichungen = d.spalten.flatMap((s) => [
    ...s.verschoben.map((x) => `verschoben: ${x}`),
    ...s.per_position.map((x) => `nach Position: ${x}`),
    ...s.fehlen.map((x) => `fehlt: ${x}`),
  ])
  const h = d.hinweise
  return (
    <div className={cn("grid gap-1.5 rounded-lg border p-3 text-[13px]", d.stopp.length ? "border-crit/40 bg-crit/5" : "border-hair/10")}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <b>{d.art ? `${ART[d.art]} ${d.jahr ?? ""}` : "Unbekannte Datei"}</b>
        <span className="text-ink2">{d.datei}</span>
        {!d.stopp.length && <span className="text-ink2">· {nf0.format(d.zeilen)} Proben</span>}
      </div>
      {d.stopp.length > 0 ? (
        <div className="text-crit">
          <b>Wird nicht eingelesen:</b>
          <ul className="m-0 mt-1 pl-4">{d.stopp.map((s) => <li key={s}>{s}</li>)}</ul>
        </div>
      ) : abweichungen.length === 0 && d.spalten.length > 0 ? (
        <span className="text-good">Spalten: alle über die Überschriften erkannt</span>
      ) : null}
      {abweichungen.length > 0 && (
        <details>
          <summary className="cursor-pointer text-ink2">{`Spalten: ${abweichungen.length} Abweichung(en) von der gewohnten Anordnung`}</summary>
          <ul className="m-0 mt-1 pl-4 text-ink2">{abweichungen.map((x) => <li key={x}>{x}</li>)}</ul>
        </details>
      )}
      {d.warnungen.length > 0 && !d.stopp.length && (
        <ul className="m-0 rounded-md bg-warn/15 py-1.5 pl-6 pr-2">{d.warnungen.map((x) => <li key={x}>{x}</li>)}</ul>
      )}
      {(h.uebersprungen || h.bis) && (
        <span className="text-ink2">
          {h.uebersprungen ?? `Daten bis ${datum(h.bis)} · in zwei Monatsblättern doppelt: ${h.dubletten ?? 0} · ohne eigenes Datum: ${h.ohne_datum ?? 0}`}
        </span>
      )}
    </div>
  )
}

function Zahlen({ e }: { e: ImportErgebnis }) {
  const werte: Array<[string, number]> = [
    ["neu", e.neu], ["geändert", e.geaendert], ["unverändert", e.unveraendert], ["ausgeblendet", e.ausgeblendet ?? 0],
  ]
  if (e.wieder_eingeblendet) werte.push(["wieder eingeblendet", e.wieder_eingeblendet])
  werte.push(["Ausreißer gesamt", e.ausreisser_gesamt])
  return (
    <div className="flex flex-wrap gap-x-7 gap-y-2 rounded-lg bg-surface2 p-4">
      {werte.map(([l, n]) => (
        <div key={l} className="grid"><b className={cn("text-xl", l === "ausgeblendet" && n > 0 && "text-crit")}>{nf0.format(n)}</b><span className="text-xs text-ink2">{l}</span></div>
      ))}
    </div>
  )
}

function Import() {
  const qc = useQueryClient()
  const [dateien, setDateien] = useState<File[]>([])
  const [eingabe, setEingabe] = useState(0) // neuer Schlüssel leert das Dateifeld
  const laeufe = useQuery({ queryKey: ["importe"], queryFn: async () => (await api.get<Array<{ zeitpunkt: string; wer: string; dateien: string[]; ergebnis: ImportErgebnis }>>("/verwaltung/importe")).data })
  const vorschau = useMutation({
    mutationFn: async (auswahl: File[]) => (await api.post<Vorschau>("/verwaltung/import/vorschau", formular(auswahl))).data,
  })
  const einlesen = useMutation({
    mutationFn: async () => (await api.post<ImportErgebnis>("/verwaltung/import", formular(dateien))).data,
    onSuccess: () => {
      setDateien([])
      setEingabe((n) => n + 1)
      vorschau.reset()
      void qc.invalidateQueries({ queryKey: ["importe"] })
      void qc.invalidateQueries({ queryKey: ["auswertung"] })
    },
  })
  function waehlen(liste: FileList | null) {
    const auswahl = Array.from(liste ?? [])
    setDateien(auswahl)
    einlesen.reset()
    if (auswahl.length) vorschau.mutate(auswahl)
    else vorschau.reset()
  }
  function verwerfen() {
    setDateien([])
    setEingabe((n) => n + 1)
    vorschau.reset()
  }
  const v = vorschau.data
  const e = v?.ergebnis
  return (
    <>
      <Karte
        titel="Excel-Datei einlesen"
        unter="Betriebstagebuch oder Jahresextrakt auswählen — Dateiname und Spaltenfolge sind egal, Spalten werden über die Überschriften erkannt. Die Vorschau zeigt, was sich ändert; erst „Jetzt einlesen“ speichert. Die Datei ist für ihr Jahr maßgeblich: Proben, die darin fehlen, werden ausgeblendet (nicht gelöscht). Am Terminal erfasste Proben bleiben unberührt."
      >
        <input key={eingabe} type="file" accept=".xlsx" multiple onChange={(ev) => waehlen(ev.target.files)} className="text-sm" aria-label="Excel-Dateien auswählen" />
        {vorschau.isPending && <p className="m-0 text-sm text-ink2">Prüft die Datei … (bei einem ganzen Jahr einige Sekunden)</p>}
        {vorschau.isError && <p className="m-0 text-sm text-crit">{fehlertext(vorschau.error)}</p>}
        {v && (
          <div className="grid gap-3">
            {v.dateien.map((d) => <DateiPruefung key={d.datei} d={d} />)}
            {e && (
              <>
                <p className="m-0 text-[13px] font-medium">Vorschau — noch nichts gespeichert:</p>
                <Zahlen e={e} />
                {(e.ausgeblendet ?? 0) > 0 && (
                  <div className="rounded-lg bg-crit/10 p-3 text-[13px]">
                    <b>{`${nf0.format(e.ausgeblendet ?? 0)} Proben aus früheren Importen fehlen in der Datei und werden ausgeblendet`}</b>
                    {" "}(nicht gelöscht; sie kommen zurück, sobald sie wieder in der Datei stehen). Ist das die vollständige, aktuelle Datei?
                    <ul className="m-0 mt-1 pl-4 text-ink2">
                      {(e.beispiele_ausgeblendet ?? []).map((b, i) => (
                        <li key={i}>{`${datum(b.datum)} ${b.zeitfenster ?? ""} · Mischung ${b.mischung ?? "–"} · ${b.nc_typ ?? "–"}`}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(e.schluessel_uebernommen ?? 0) > 0 && (
                  <p className="m-0 text-[12.5px] text-ink2">{`${nf0.format(e.schluessel_uebernommen ?? 0)} Proben aus Importen vor dieser Version wiedererkannt.`}</p>
                )}
              </>
            )}
            <div className="flex flex-wrap gap-2">
              <Knopf variante="primaer" disabled={v.stopp || einlesen.isPending} onClick={() => einlesen.mutate()}>
                {einlesen.isPending ? "Liest ein …" : "Jetzt einlesen"}
              </Knopf>
              <Knopf onClick={verwerfen} disabled={einlesen.isPending}>Verwerfen</Knopf>
            </div>
          </div>
        )}
        {einlesen.isError && <p className="m-0 text-sm text-crit">{fehlertext(einlesen.error)}</p>}
        {einlesen.data && (
          <div className="grid gap-2">
            <p className="m-0 text-[13px] font-medium text-good">Eingelesen.</p>
            <Zahlen e={einlesen.data} />
          </div>
        )}
      </Karte>
      <Karte titel="Letzte Importe">
        {!laeufe.data ? <Leer>Wird geladen …</Leer> : laeufe.data.length === 0 ? <Leer>Noch kein Import.</Leer> : (
          <table className="w-full border-collapse text-[13px]">
            <thead><tr className="text-left text-[11.5px] text-muted">{["Zeitpunkt", "Wer", "Dateien", "neu", "geändert", "unverändert", "ausgeblendet"].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {laeufe.data.map((l) => (
                <tr key={l.zeitpunkt} className="border-t border-grid tabular">
                  <td className="px-2 py-2">{datumZeit(l.zeitpunkt)}</td>
                  <td className="px-2 py-2">{l.wer}</td>
                  <td className="px-2 py-2 text-ink2">{l.dateien.join(", ")}</td>
                  <td className="px-2 py-2">{nf0.format(l.ergebnis.neu)}</td>
                  <td className="px-2 py-2">{nf0.format(l.ergebnis.geaendert)}</td>
                  <td className="px-2 py-2">{nf0.format(l.ergebnis.unveraendert)}</td>
                  <td className="px-2 py-2">{l.ergebnis.ausgeblendet === undefined ? "–" : nf0.format(l.ergebnis.ausgeblendet)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Karte>
    </>
  )
}
